import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  UserCheck,
  RefreshCw,
  Loader2,
  MessageSquarePlus,
  Shield,
  UserMinus,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Members who joined within this window get the welcome-draft prompt.
// Beyond that, the Chat button opens the DM silently — a warm hello
// feels awkward for someone who joined three weeks ago.
const WELCOME_WINDOW_MS = 24 * 60 * 60 * 1000;

const WELCOME_TEMPLATE_KEY = 'ghostcomm_welcome_template';

// Built-in fallback. Used when the user hasn't customised their own
// template via Settings → Privacy → Welcome message.
const BUILTIN_WELCOME =
  'Hey{who} — glad you made it onto Government Shield.\n\n' +
  'This chat is end-to-end encrypted; use it as the default way to ' +
  'reach me from now on. 🛡';

const defaultGreeting = (name) => {
  const who = name ? `, ${name}` : '';
  let tpl;
  try {
    tpl = localStorage.getItem(WELCOME_TEMPLATE_KEY);
  } catch {
    tpl = null;
  }
  const source = tpl && tpl.trim() ? tpl : BUILTIN_WELCOME;
  // Template placeholders:
  //   {name} — recipient's display name (or blank)
  //   {who}  — ", <name>" when name is known; blank otherwise
  //            (lets the built-in template read naturally even when
  //            the recipient username is missing)
  return source.replaceAll('{name}', name || '').replaceAll('{who}', who);
};

// Relative "X ago" formatter — same vernacular as ReferralChainList so
// the two widgets feel like one surface.
const fmtAge = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return 'just now';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
};

/**
 * InviteRedemptionsList — a celebratory, always-visible surface on the
 * Invites tab that shows who accepted your direct invites (and when).
 * Closes the one-way feeling of the invite flow: the sharer gets a
 * live confirmation the moment a friend joins, without needing a push
 * or email.
 *
 * Data source: reuses `GET /api/users/me/referrals` (BFS tree) and
 * filters to `depth === 1` live members — i.e. accounts the current
 * user directly vouched for. Tombstones (deleted invitees) are hidden
 * here because this widget is about "people who joined because of you",
 * which is materially a different frame from the full chain-of-trust
 * audit the ReferralChainList serves.
 */
export const InviteRedemptionsList = ({ isActive, onStartChat }) => {
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  // Per-row "starting chat…" spinner so clicks feel responsive even
  // when the conversations endpoint takes a beat.
  const [startingFor, setStartingFor] = useState(null);
  // Welcome-draft dialog state: when the user hits Chat on a <24h
  // invitee we open a quick editable greeting card so the first
  // message lands warm instead of a blank blinking cursor.
  const [welcomeFor, setWelcomeFor] = useState(null); // { id, username }
  const [welcomeDraft, setWelcomeDraft] = useState('');
  // Disinvite confirmation: hoisted state so closing the dialog is
  // controlled by an AlertDialog rather than a per-row local var.
  const [disinviteFor, setDisinviteFor] = useState(null); // { id, username }
  const [disinviting, setDisinviting] = useState(false);
  // After a successful disinvite, if the caller admins any group
  // conversations the disinvitee is still in, we surface a follow-up
  // dialog letting them tidy those rooms in one go. Sealed Sender
  // means we can't redact past messages — but we *can* remove the
  // disinvitee from participants so they can't keep talking to the
  // room. The user picks per-group; nothing happens silently.
  const [cleanupCtx, setCleanupCtx] = useState(null);
  // shape: { username, groups: [{id, encrypted_name, participant_count}], selected: Set<id>, busy: bool }

  const performDisinvite = async () => {
    if (!disinviteFor || disinviting) return;
    setDisinviting(true);
    try {
      await axios.post(
        `${API}/users/me/invitees/${disinviteFor.id}/disinvite`,
      );
      toast.success(
        `${disinviteFor.username || 'They'} have been disinvited.`,
      );
      // Drop the row optimistically.
      setNodes((prev) => prev.filter((n) => n.id !== disinviteFor.id));
      // Probe for shared admin'd groups — if any, open the cleanup
      // follow-up. Failing this lookup is non-fatal; the disinvite
      // itself already succeeded.
      try {
        const res = await axios.get(
          `${API}/conversations/admin-shared-with/${disinviteFor.id}`,
        );
        const groups = res.data?.groups || [];
        if (groups.length > 0) {
          setCleanupCtx({
            user_id: disinviteFor.id,
            username: disinviteFor.username,
            groups,
            selected: new Set(groups.map((g) => g.id)), // pre-select all
            busy: false,
          });
        }
      } catch {
        /* non-fatal */
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Disinvite failed');
    } finally {
      setDisinviting(false);
      setDisinviteFor(null);
    }
  };

  const performCleanup = async () => {
    if (!cleanupCtx || cleanupCtx.busy) return;
    setCleanupCtx((c) => ({ ...c, busy: true }));
    const targets = Array.from(cleanupCtx.selected);
    let cleaned = 0;
    let failed = 0;
    for (const convId of targets) {
      try {
        await axios.post(
          `${API}/conversations/${convId}/remove-participant`,
          { user_id: cleanupCtx.user_id },
        );
        cleaned += 1;
      } catch {
        failed += 1;
      }
    }
    if (cleaned > 0) {
      toast.success(
        `Removed from ${cleaned} group${cleaned === 1 ? '' : 's'}${
          failed ? ` (${failed} failed)` : ''
        }`,
      );
    } else if (failed > 0) {
      toast.error(`All ${failed} removals failed`);
    }
    setCleanupCtx(null);
  };

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/users/me/referrals?max_depth=1`);
      const direct = (res.data?.nodes || [])
        .filter((n) => n.depth === 1 && !n.deleted)
        .sort((a, b) => new Date(b.joined_at || 0) - new Date(a.joined_at || 0));
      setNodes(direct);
    } catch (_e) {
      // Non-fatal — keep prior state; user can retry via the refresh button.
    } finally {
      setLoading(false);
      setLoadedOnce(true);
    }
  }, []);

  // Refetch whenever the tab becomes active (cheap; the endpoint is
  // BFS-capped at 500 nodes and we only request depth=1). Also refetch
  // every 20s while the tab is active so a newly-redeemed invite
  // surfaces without the user having to close/reopen the dialog.
  useEffect(() => {
    if (!isActive) return;
    fetch();
    const iv = setInterval(fetch, 20000);
    return () => clearInterval(iv);
  }, [isActive, fetch]);

  const count = nodes.length;
  const header = useMemo(() => {
    if (!loadedOnce && loading) return 'Checking for new redemptions…';
    if (count === 0) return 'Nobody has redeemed your invites yet';
    if (count === 1) return '1 person joined via your invite';
    return `${count} people joined via your invite`;
  }, [count, loadedOnce, loading]);

  return (
    <div className="space-y-2" data-testid="invite-redemptions-list">
      <div className="flex items-center justify-between">
        <label className="text-sm text-slate-300 flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-emerald-400" />
          {header}
        </label>
        <Button
          variant="ghost"
          size="icon"
          onClick={fetch}
          disabled={loading}
          className="h-7 w-7 text-slate-400 hover:text-white"
          title="Refresh"
          data-testid="invite-redemptions-refresh"
        >
          {loading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5" />
          )}
        </Button>
      </div>

      {count === 0 ? (
        <p
          className="text-xs text-slate-500 italic px-2 py-3"
          data-testid="invite-redemptions-empty"
        >
          Share an invite above — when someone redeems it, they'll show up
          here.
        </p>
      ) : (
        <ScrollArea className="max-h-40">
          <div className="space-y-1.5">
            {nodes.map((n) => (
              <div
                key={n.id}
                className="flex items-center gap-2 p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/10"
                data-testid={`invite-redemption-${n.id}`}
              >
                <Avatar className="h-7 w-7 shrink-0">
                  <AvatarFallback className="bg-emerald-500/20 text-emerald-300 text-xs">
                    {(n.username || '?')[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-white truncate">
                    {n.username || 'Member'}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    Joined {fmtAge(n.joined_at)}
                    {n.direct_referrals > 0 && (
                      <span className="ml-2 text-emerald-400/70">
                        · vouched for {n.direct_referrals}
                      </span>
                    )}
                  </p>
                </div>
                {onStartChat && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      if (startingFor) return;
                      const joinedAt = n.joined_at
                        ? new Date(n.joined_at).getTime()
                        : 0;
                      const isFresh =
                        joinedAt && Date.now() - joinedAt <= WELCOME_WINDOW_MS;
                      if (isFresh) {
                        // Open the welcome-draft card so the user can
                        // tweak the greeting before we open the DM.
                        setWelcomeDraft(defaultGreeting(n.username));
                        setWelcomeFor({ id: n.id, username: n.username });
                        return;
                      }
                      setStartingFor(n.id);
                      try {
                        await onStartChat({ id: n.id, username: n.username });
                      } finally {
                        setStartingFor(null);
                      }
                    }}
                    disabled={startingFor === n.id}
                    className="h-7 px-2 text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10 shrink-0"
                    title={`Say hello to ${n.username || 'this member'}`}
                    data-testid={`invite-redemption-chat-${n.id}`}
                  >
                    {startingFor === n.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <MessageSquarePlus className="w-3.5 h-3.5" />
                    )}
                    <span className="ml-1 text-[11px] font-medium">Chat</span>
                  </Button>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
      )}

      {/* Welcome-draft dialog — editable greeting that gets pre-filled
          into the chat's compose buffer once the DM opens. Never
          auto-sends; the user still has to hit Send so anti-keylogger
          + encryption guarantees are preserved. */}
      <Dialog
        open={!!welcomeFor}
        onOpenChange={(open) => {
          if (!open) {
            setWelcomeFor(null);
            setWelcomeDraft('');
          }
        }}
      >
        <DialogContent className="bg-card border-border max-w-md" data-testid="welcome-draft-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-400" />
              Say hi to {welcomeFor?.username || 'your new contact'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Pre-filled greeting below. Edit it, then open the chat — you'll
              see it ready to send, but nothing is transmitted until you hit
              Send yourself.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={welcomeDraft}
            onChange={(e) => setWelcomeDraft(e.target.value)}
            rows={5}
            className="bg-secondary/40 border-border text-white font-normal resize-none"
            placeholder="Write a quick hello…"
            data-testid="welcome-draft-textarea"
            maxLength={500}
          />
          <div className="text-[11px] text-slate-500 text-right -mt-2">
            {welcomeDraft.length} / 500
          </div>
          <DialogFooter className="flex-row justify-between gap-2 sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={async () => {
                const target = welcomeFor;
                setWelcomeFor(null);
                setWelcomeDraft('');
                if (!target) return;
                setStartingFor(target.id);
                try {
                  await onStartChat({ id: target.id, username: target.username });
                } finally {
                  setStartingFor(null);
                }
              }}
              data-testid="welcome-draft-skip"
            >
              Just open chat
            </Button>
            <Button
              type="button"
              onClick={async () => {
                const target = welcomeFor;
                const text = welcomeDraft;
                setWelcomeFor(null);
                setWelcomeDraft('');
                if (!target) return;
                setStartingFor(target.id);
                try {
                  await onStartChat({ id: target.id, username: target.username });
                  // Dispatch on the next tick so the DM has mounted
                  // its compose listener before we push the draft.
                  if (text) {
                    setTimeout(() => {
                      window.dispatchEvent(
                        new CustomEvent('gs:compose-prefill', {
                          detail: { text },
                        })
                      );
                    }, 120);
                  }
                } finally {
                  setStartingFor(null);
                }
              }}
              disabled={!welcomeDraft.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="welcome-draft-open"
            >
              Open with draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!disinviteFor}
        onOpenChange={(open) => {
          if (!open && !disinviting) setDisinviteFor(null);
        }}
      >
        <AlertDialogContent
          className="bg-card border-border"
          data-testid="invite-redemption-disinvite-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>
              Disinvite {disinviteFor?.username || 'this member'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Takes back your vouch.{' '}
              <span className="font-mono text-white">
                {disinviteFor?.username || 'They'}
              </span>{' '}
              will be{' '}
              <span className="text-red-400 font-semibold">
                disabled — they can't log in or be messaged
              </span>
              . Their account row stays for audit, and the server owner can
              re-enable them later if you change your mind.
              <br />
              <br />
              This is the right tool for "I made a mistake inviting them."
              For a security incident affecting an entire branch of the
              tree, ask the server owner to use admin → revoke with cascade.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={disinviting}
              data-testid="invite-redemption-disinvite-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                performDisinvite();
              }}
              disabled={disinviting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="invite-redemption-disinvite-confirm"
            >
              {disinviting ? 'Disinviting…' : 'Disinvite'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!cleanupCtx}
        onOpenChange={(open) => {
          if (!open && !cleanupCtx?.busy) setCleanupCtx(null);
        }}
      >
        <AlertDialogContent
          className="bg-card border-border max-w-lg"
          data-testid="disinvite-cleanup-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>
              Also remove {cleanupCtx?.username || 'them'} from your groups?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You admin{' '}
              <span className="text-white font-semibold">
                {cleanupCtx?.groups?.length || 0} group
                {cleanupCtx?.groups?.length === 1 ? '' : 's'}
              </span>{' '}
              they're still in. Untick any you'd rather leave alone.
              <br />
              <br />
              <span className="text-amber-300/90 text-xs">
                Past messages stay — Sealed Sender means the server can't
                identify which messages were theirs. This stops them from
                sending new ones to the room.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 max-h-60 overflow-auto py-2">
            {(cleanupCtx?.groups || []).map((g) => {
              const checked = cleanupCtx?.selected?.has(g.id);
              return (
                <label
                  key={g.id}
                  className="flex items-center gap-3 p-2 rounded-md bg-secondary/30 hover:bg-secondary/50 cursor-pointer transition-colors"
                  data-testid={`disinvite-cleanup-row-${g.id}`}
                >
                  <input
                    type="checkbox"
                    checked={!!checked}
                    onChange={(e) => {
                      const want = e.target.checked;
                      setCleanupCtx((c) => {
                        if (!c) return c;
                        const next = new Set(c.selected);
                        if (want) next.add(g.id);
                        else next.delete(g.id);
                        return { ...c, selected: next };
                      });
                    }}
                    disabled={cleanupCtx?.busy}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">
                      {g.encrypted_name || 'Untitled group'}
                    </p>
                    <p className="text-[11px] text-slate-500 font-mono">
                      {g.participant_count} member
                      {g.participant_count === 1 ? '' : 's'}
                    </p>
                  </div>
                </label>
              );
            })}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={cleanupCtx?.busy}
              data-testid="disinvite-cleanup-skip"
            >
              Skip cleanup
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                performCleanup();
              }}
              disabled={
                cleanupCtx?.busy ||
                (cleanupCtx?.selected?.size ?? 0) === 0
              }
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="disinvite-cleanup-confirm"
            >
              {cleanupCtx?.busy
                ? 'Removing…'
                : `Remove from ${cleanupCtx?.selected?.size || 0} group${
                    cleanupCtx?.selected?.size === 1 ? '' : 's'
                  }`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InviteRedemptionsList;
